import React, { useState, useEffect, useRef } from 'react';
import { initialGameState } from './state.js';
import { allIcons, iconPairs, getInitialIcons, shuffleArray } from './data.js';
import Instructions from './instructions.jsx';
import { VideoOnboarding } from './components/VideoOnboarding.jsx';
import { Onboarding } from './components/Onboarding.jsx';
import { ProgressBar } from './components/ProgressBar.jsx';
import { Platform } from './components/Platform.jsx';
import { CategoryIcons } from './components/CategoryIcons.jsx';
import { Controls } from './components/Controls.jsx';
import { HintTooltip } from './components/HintTooltip.jsx';
import { ProductModal } from './components/ProductModal.jsx';
import { SparkOverlay } from './components/SparkOverlay.jsx';
import { ResultIcon } from './components/ResultIcon.jsx';
import { InitialScreen, PlayNowButton, DemoScreen, CloseFullscreenButton } from './components/InitialScreen.jsx';
import { Logo } from './logo.jsx';

// Sound files
const sounds = {
    bgLoop: new Audio('./images/Game_Sounds/BG_loop.mp3'),
    hintAppear: new Audio('./images/Game_Sounds/Hint_Appear.mp3'),
    iconReturn: new Audio('./images/Game_Sounds/Icon_return.mp3'),
    iconPickup: new Audio('./images/Game_Sounds/icon_pickup.mp3'),
    productMerge: new Audio('./images/Game_Sounds/product_merge.mp3'),
    rightDrop: new Audio('./images/Game_Sounds/right_drop.mp3'),
    levelCompleted: new Audio('./images/Game_Sounds/Level_Completed.mp3'),
    puzzleComplete: new Audio('./images/Game_Sounds/Puzzle_Game_Level_Complete.wav'),
    levelUp: new Audio('./images/Game_Sounds/level_up.wav')
};

// Configure background loop
sounds.bgLoop.loop = true;
sounds.bgLoop.volume = 0.3;

// Main App Component with State Management
export function App() {
    const [gameState, setGameState] = useState(initialGameState);
    const [visibleIcons, setVisibleIcons] = useState([]);
    const [remainingIcons, setRemainingIcons] = useState([]);
    const [leftIcon, setLeftIcon] = useState(null);
    const [rightIcon, setRightIcon] = useState(null);
    const [draggedIcon, setDraggedIcon] = useState(null);
    const [usedIcons, setUsedIcons] = useState([]);
    const [matchedPairs, setMatchedPairs] = useState([]);
    const [completedPairs, setCompletedPairs] = useState([]);
    const [matchedCategory, setMatchedCategory] = useState(null);
    const [hideSpheresForMerge, setHideSpheresForMerge] = useState(false);
    const [showResultIcon, setShowResultIcon] = useState(false);
    const [flyingResultIcon, setFlyingResultIcon] = useState(null); // { product, startPos, endPos }
    const [resultProduct, setResultProduct] = useState(null);
    const [currentMatchedProduct, setCurrentMatchedProduct] = useState(null);
    const [showFinalCongrats, setShowFinalCongrats] = useState(false); // Track if we should show final congratulations
    const [incorrectAttempts, setIncorrectAttempts] = useState(0);
    const [hintEnabled, setHintEnabled] = useState(false);
    const [hintHighlight, setHintHighlight] = useState(false); // Track hint icon highlight state
    const [highlightedIconId, setHighlightedIconId] = useState(null);
    const [correctIconHintId, setCorrectIconHintId] = useState(null); // For 3-retry hint (green color only, no glow)
    const [hintAutoShown, setHintAutoShown] = useState(false); // Track if hint was auto-shown (not manually clicked)
    const [showVideoOnboarding, setShowVideoOnboarding] = useState(false);
    const [showInteractiveOnboarding, setShowInteractiveOnboarding] = useState(false);
    const [onboardingStep, setOnboardingStep] = useState(0); // 0: not started, 1: highlight first icon, 2: highlight second icon, 3: complete
    const [allowedDragIconId, setAllowedDragIconId] = useState(null); // Only this icon can be dragged during onboarding
    const [highlightedSphere, setHighlightedSphere] = useState(null); // 'left' or 'right'
    const [gameStarted, setGameStarted] = useState(false); // Track if game has actually started (after onboarding)
    const [isDraggingEnabled, setIsDraggingEnabled] = useState(true); // Control drag functionality
    const [gameStartTime, setGameStartTime] = useState(null); // Track when game starts
    const [gameEndTime, setGameEndTime] = useState(null); // Track when game ends
    const [elapsedTime, setElapsedTime] = useState(0); // Elapsed time in seconds
    const [isFullscreen, setIsFullscreen] = useState(false); // Track fullscreen state
    const leftSphereRef = useRef(null);
    const rightSphereRef = useRef(null);
    
    // Auto-enter fullscreen on any click (except close button and initial screen)
    useEffect(() => {
        const handleAppClick = (e) => {
            // Don't enter fullscreen if on initial screen (only Play Now button should trigger it)
            if (gameState.initialScreen) {
                return;
            }
            
            // Don't enter fullscreen if clicking the close button
            if (e.target.closest('.close-fullscreen-btn')) {
                return;
            }
            
            // Don't enter fullscreen if already in fullscreen
            if (isFullscreen) {
                return;
            }
            
            // Enter fullscreen on any other click
            const element = document.documentElement;
            
            if (element.requestFullscreen) {
                element.requestFullscreen().catch((err) => {
                    console.log('Fullscreen request failed:', err);
                });
            } else if (element.webkitRequestFullscreen) {
                element.webkitRequestFullscreen();
            } else if (element.msRequestFullscreen) {
                element.msRequestFullscreen();
            }
            
            setIsFullscreen(true);
        };
        
        const appElement = document.getElementById('app');
        if (appElement && !gameState.initialScreen) {
            appElement.addEventListener('click', handleAppClick);
        }
        
        return () => {
            if (appElement) {
                appElement.removeEventListener('click', handleAppClick);
            }
        };
    }, [isFullscreen, gameState.initialScreen]);
    
    // Play sound helper function
    const playSound = (sound) => {
        if (gameState.soundEnabled && sound) {
            sound.currentTime = 0;
            sound.play().catch(err => console.log('Sound play failed:', err));
        }
    };
    
    // Helper to play level up sound when pick list is rendered
    const playLevelUpSound = () => {
        if (gameState.soundEnabled && !gameState.initialScreen) {
            sounds.levelUp.currentTime = 0;
            sounds.levelUp.play().catch(err => console.log('Level up sound failed:', err));
        }
    };
    
    // Background music control
    useEffect(() => {
        // Play music when: not on initial screen, sound enabled, and either game started OR in onboarding
        const shouldPlayMusic = !gameState.initialScreen && gameState.soundEnabled && 
                                (gameStarted || showInteractiveOnboarding || showVideoOnboarding);
        
        if (shouldPlayMusic) {
            sounds.bgLoop.play().catch(err => console.log('BG music play failed:', err));
        } else {
            sounds.bgLoop.pause();
        }
        
        return () => {
            sounds.bgLoop.pause();
        };
    }, [gameState.initialScreen, gameState.soundEnabled, gameStarted, showInteractiveOnboarding, showVideoOnboarding]);
    
    // Initialize game with random 5 pairs (10 icons total)
    useEffect(() => {
        if (!gameState.initialScreen && visibleIcons.length === 0 && !showInteractiveOnboarding) {
            // Shuffle the pairs array and take first 5 pairs
            const shuffledPairs = shuffleArray([...iconPairs]);
            const selectedPairs = shuffledPairs.slice(0, 5);
            const remainingPairs = shuffledPairs.slice(5);
            
            // Get all icons from selected pairs
            const selectedIcons = selectedPairs.flatMap(pair => 
                allIcons.filter(icon => icon.productId === pair.id)
            );
            
            // Get all icons from remaining pairs
            const remainingIconsFromPairs = remainingPairs.flatMap(pair =>
                allIcons.filter(icon => icon.productId === pair.id)
            );
            
            // Shuffle the selected icons before displaying
            setVisibleIcons(shuffleArray(selectedIcons));
            setRemainingIcons(shuffleArray(remainingIconsFromPairs));
        }
    }, [gameState.initialScreen, visibleIcons.length, showInteractiveOnboarding]);
    
    // Disable non-highlighted icons during onboarding
    useEffect(() => {
        if (!showInteractiveOnboarding || visibleIcons.length === 0) {
            return;
        }
        
        // During onboarding, add all icons except highlighted one to usedIcons
        if (highlightedIconId) {
            const iconsToDisable = visibleIcons
                .filter(icon => icon.id !== highlightedIconId)
                .map(icon => icon.id);
            setUsedIcons(iconsToDisable);
        }
        
        return () => {
            // Cleanup: clear used icons when onboarding ends
            if (!showInteractiveOnboarding) {
                setUsedIcons([]);
            }
        };
    }, [showInteractiveOnboarding, highlightedIconId, visibleIcons]);
    
    // Auto-hide hint after 5 seconds (only if auto-shown, not manually clicked)
    useEffect(() => {
        if (gameState.showHint && hintAutoShown) {
            const timer = setTimeout(() => {
                setGameState(prev => ({ ...prev, showHint: false }));
                setHintAutoShown(false);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [gameState.showHint, hintAutoShown]);
    
    // Auto-reset hint highlight after 3 seconds
    useEffect(() => {
        if (hintHighlight) {
            const timer = setTimeout(() => {
                setHintHighlight(false);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [hintHighlight]);
    
    // Auto-hide spark animation
    useEffect(() => {
        if (gameState.showSpark) {
            const timer = setTimeout(() => {
                setGameState(prev => ({ ...prev, showSpark: false }));
            }, 800);
            return () => clearTimeout(timer);
        }
    }, [gameState.showSpark]);
    
    // Toggle modal-active class on app element
    useEffect(() => {
        const appElement = document.getElementById('app');
        if (appElement) {
            if (gameState.showModal) {
                appElement.classList.add('modal-active');
            } else {
                appElement.classList.remove('modal-active');
            }
        }
    }, [gameState.showModal]);
    
    // Timer logic - start when game starts, update every second
    useEffect(() => {
        let timer;
        if (gameStartTime && !gameEndTime) {
            timer = setInterval(() => {
                setElapsedTime(Math.floor((Date.now() - gameStartTime) / 1000));
            }, 1000);
        }
        
        return () => {
            if (timer) clearInterval(timer);
        };
    }, [gameStartTime, gameEndTime]);
    
    // Smooth flying animation for result icon
    useEffect(() => {
        if (flyingResultIcon) {
            // Small delay to ensure the element is rendered with initial position
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    const flyingElement = document.querySelector('.flying-result-icon');
                    if (flyingElement) {
                        // Trigger the transition to final position with smaller scale to fit keyhole
                        flyingElement.style.transform = 'translate(0, 0) scale(0.4)';
                        flyingElement.style.opacity = '1';
                    }
                });
            });
        }
    }, [flyingResultIcon]);
    
    // Handlers
    const handleExitFullscreen = () => {
        // Just exit fullscreen, don't reset game
        setIsFullscreen(false);
    };

    const handleStartGame = () => {
        // Enter fullscreen when clicking Play Now
        const element = document.documentElement;
        
        if (element.requestFullscreen) {
            element.requestFullscreen().catch((err) => {
                console.log('Fullscreen request failed:', err);
            });
        } else if (element.webkitRequestFullscreen) {
            element.webkitRequestFullscreen();
        } else if (element.msRequestFullscreen) {
            element.msRequestFullscreen();
        }
        
        setIsFullscreen(true);
        setGameState(prev => ({ 
            ...prev, 
            showInstructions: true
        }));
    };
    
    const handleCloseInstructions = () => {
        // Enter fullscreen if not already in fullscreen
        if (!isFullscreen) {
            const element = document.documentElement;
            
            if (element.requestFullscreen) {
                element.requestFullscreen().catch((err) => {
                    console.log('Fullscreen request failed:', err);
                });
            } else if (element.webkitRequestFullscreen) {
                element.webkitRequestFullscreen();
            } else if (element.msRequestFullscreen) {
                element.msRequestFullscreen();
            }
            
            setIsFullscreen(true);
        }
        
        // Start guided onboarding
        setGameState(prev => ({ 
            ...prev, 
            showInstructions: false,
            initialScreen: false,
            currentProductIndex: 0
        }));
        
        // Set up onboarding with a specific pair (Zoho Mail - envelope + computer)
        const zohoMailPair = iconPairs.find(pair => pair.id === 0);
        
        // Create a fixed set of icons for onboarding
        const onboardingPairs = [
            zohoMailPair, // Zoho Mail - will be the match
            iconPairs[1],
            iconPairs[2],
            iconPairs[3],
            iconPairs[4]
        ];
        
        const onboardingIcons = onboardingPairs.flatMap(pair => 
            allIcons.filter(icon => icon.productId === pair.id)
        );
        
        // Fixed order: Put envelope first, computer second
        const sortedOnboardingIcons = [...onboardingIcons].sort((a, b) => {
            if (a.iconName === 'envelope') return -1;
            if (b.iconName === 'envelope') return 1;
            if (a.iconName === 'computer') return -1;
            if (b.iconName === 'computer') return 1;
            return 0;
        });
        
        setVisibleIcons(sortedOnboardingIcons);
        setShowInteractiveOnboarding(true);
        
        // Start onboarding: Step 1 - Highlight first icon (envelope) and left sphere
        setTimeout(() => {
            const envelopeIcon = sortedOnboardingIcons.find(icon => icon.iconName === 'envelope');
            if (envelopeIcon) {
                setOnboardingStep(1);
                setHighlightedIconId(envelopeIcon.id);
                setAllowedDragIconId(envelopeIcon.id);
                setHighlightedSphere('left');
            }
        }, 500);
    };
    
    const handlePlayNow = () => {
        setGameState(prev => ({ 
            ...prev, 
            showInstructions: false,
            initialScreen: false,
            currentProductIndex: 0
        }));
        
        // Set up fixed onboarding icons with Zoho Mail (envelope + computer)
        // Get the Zoho Mail pair (id: 0)
        const zohoMailPair = iconPairs.find(pair => pair.id === 0);
        
        // Create a fixed set of icons for onboarding
        // Include both Zoho Mail icons (envelope, computer) and some other icons
        const onboardingPairs = [
            zohoMailPair, // Zoho Mail - will be the match
            iconPairs[1], // Add a few more for variety
            iconPairs[2],
            iconPairs[3],
            iconPairs[4]
        ];
        
        const onboardingIcons = onboardingPairs.flatMap(pair => 
            allIcons.filter(icon => icon.productId === pair.id)
        );
        
        // Fixed order: Put envelope first, computer second for consistent onboarding
        const sortedOnboardingIcons = [...onboardingIcons].sort((a, b) => {
            if (a.iconName === 'envelope') return -1;
            if (b.iconName === 'envelope') return 1;
            if (a.iconName === 'computer') return -1;
            if (b.iconName === 'computer') return 1;
            return 0;
        });
        
        setVisibleIcons(sortedOnboardingIcons);
        
        // if (isMobile) {
        //     // Show video onboarding on mobile
        //     setShowVideoOnboarding(true);
        // } else {
            // Show interactive onboarding on desktop
            setShowInteractiveOnboarding(true);
        // }
    };
    
    const handleVideoComplete = () => {
        // After video onboarding, go directly to game (no interactive onboarding)
        setShowVideoOnboarding(false);
        
        // Start background music
        setGameStarted(true);
        
        // Start the game with fresh pick list
        const shuffledPairs = shuffleArray([...iconPairs]);
        const selectedPairs = shuffledPairs.slice(0, 5);
        const remainingPairs = shuffledPairs.slice(5);
        
        const selectedIcons = selectedPairs.flatMap(pair => 
            allIcons.filter(icon => icon.productId === pair.id)
        );
        
        const remainingIconsFromPairs = remainingPairs.flatMap(pair =>
            allIcons.filter(icon => icon.productId === pair.id)
        );
        
        setVisibleIcons(shuffleArray(selectedIcons));
        setRemainingIcons(shuffleArray(remainingIconsFromPairs));
        setUsedIcons([]);
        setCompletedPairs([]);
        setGameState(prev => ({
            ...prev,
            selectedProductIcon: null,
            selectedDropIcon: null,
            productMatched: false,
            showProductReveal: false,
            score: 0,
            failedAttempts: 0,
            showOnboarding: false
        }));
    };
    
    const handleCompleteOnboarding = () => {
        // After interactive onboarding, count the Mail match as 1/10 and start game with fresh pick list
        
        // Remove active class from app element
        const appElement = document.getElementById('app');
        if (appElement) {
            appElement.classList.remove('active');
        }
        
        // Start background music
        setGameStarted(true);
        
        setShowInteractiveOnboarding(false);
        setOnboardingStep(0);
        setHighlightedIconId(null);
        setCorrectIconHintId(null);
        setAllowedDragIconId(null);
        setHighlightedSphere(null);
        
        // Keep Mail (id: 0) as the first completed product
        const mailProductId = 0;
        
        setGameState(prev => ({ 
            ...prev, 
            showOnboarding: false,
            showModal: false,
            showSpark: false,
            completedProducts: 1, // Mail counts as 1/10
            unlockedSlots: [mailProductId], // Mail is unlocked in decoded list
            hasPendingNext: false, // Reset pending next flag
            fromDecodedList: false, // Reset decoded list flag
            viewingDuringMatch: false // Reset viewing during match flag
        }));
        
        // Clear sphere states from onboarding
        setLeftIcon(null);
        setRightIcon(null);
        setHideSpheresForMerge(false);
        setShowResultIcon(false);
        setMatchedCategory(null);
        
        // Clear sphere visuals
        const leftSphereIcon = document.getElementById('left-sphere-icon');
        const rightSphereIcon = document.getElementById('right-sphere-icon');
        if (leftSphereIcon) leftSphereIcon.style.backgroundImage = 'none';
        if (rightSphereIcon) rightSphereIcon.style.backgroundImage = 'none';
        
        // Remove merge animation classes
        const sphereContainer = document.querySelector('.sphere-container');
        if (sphereContainer) {
            sphereContainer.classList.remove('merging');
            const leftSphere = document.querySelector('.sphere:first-child');
            const rightSphere = document.querySelector('.sphere:last-child');
            if (leftSphere) leftSphere.classList.remove('merge-left');
            if (rightSphere) rightSphere.classList.remove('merge-right');
        }
        
        // Generate fresh pick list EXCLUDING Mail (since it's already found)
        const shuffledPairs = shuffleArray([...iconPairs.filter(pair => pair.id !== mailProductId)]);
        const selectedPairs = shuffledPairs.slice(0, 5);
        const remainingPairs = shuffledPairs.slice(5);
        
        // Get all icons from selected pairs
        const selectedIcons = selectedPairs.flatMap(pair => 
            allIcons.filter(icon => icon.productId === pair.id)
        );
        
        // Get all icons from remaining pairs
        const remainingIconsFromPairs = remainingPairs.flatMap(pair =>
            allIcons.filter(icon => icon.productId === pair.id)
        );
        
        // Set fresh icons and update counters to count Mail as first match
        setVisibleIcons(shuffleArray(selectedIcons));
        setRemainingIcons(shuffleArray(remainingIconsFromPairs));
        setUsedIcons([]); // Clear used icons
        setCompletedPairs([mailProductId]); // Mail is completed
        setMatchedPairs([mailProductId]); // Mail is matched
        
        // Re-enable dragging for the actual game
        setIsDraggingEnabled(true);
        
        // Start the game timer
        setGameStartTime(Date.now());
        
        console.log('✅ Onboarding complete - Mail counted as 1/10');
        console.log('New pick list with 10 fresh icons (excluding Mail)');
        console.log('Decoded products: 1/10 (Mail unlocked)');
        console.log('Remaining to find: 9 products');
    };
    
    const handleProductClick = (product) => {
        // Don't allow clicking decoded products during onboarding
        if (gameState.showOnboarding || showInteractiveOnboarding) {
            return;
        }
        
        // Toggle modal for the clicked product from decoded list
        setGameState(prev => {
            // Check if there's a pending "Find next" (match was made but not yet continued)
            const hasPendingNext = prev.hasPendingNext;
            
            // If modal is showing the same product AND it was opened from decoded list (not from actual match)
            // AND there's no pending "Find Next", then close it
            if (prev.showModal && prev.currentProduct?.id === product.id && prev.fromDecodedList && !hasPendingNext) {
                return {
                    ...prev,
                    showModal: false,
                    fromDecodedList: false
                };
            }
            
            // If clicking the same product while there's a pending "Find Next" AND modal is showing, don't close
            if (hasPendingNext && prev.currentProduct?.id === product.id && prev.showModal) {
                return prev; // No change - keep modal open showing the matched product
            }
            
            // Allow switching to a different product from decoded list
            // If there's a pending action, preserve it for the "Find Next" button
            return {
                ...prev,
                showModal: true,
                currentProduct: product,
                fromDecodedList: true,
                viewingDuringMatch: hasPendingNext // Preserve the pending action state
            };
        });
    };
    
    const handleToggleSound = () => {
        setGameState(prev => ({ ...prev, soundEnabled: !prev.soundEnabled }));
    };
    
    const handleShowHint = () => {
        // Only allow hint if it's enabled (after first incorrect attempt)
        if (!hintEnabled) return;
        
        // Play hint appear sound
        playSound(sounds.hintAppear);
        
        if (leftIcon && !rightIcon) {
            // Left sphere is filled, give hint about what to match
            const pair = iconPairs.find(p => p.id === leftIcon.productId);
            if (pair) {
                setGameState(prev => ({ 
                    ...prev, 
                    showHint: !gameState.showHint,
                    hintText: pair.hint || `Look for the matching icon to complete ${pair.productName}!`
                }));
                setHintAutoShown(false); // Manual click, so don't auto-hide
            }
        } else if (!leftIcon) {
            // No icons placed yet, give general hint
            setGameState(prev => ({ 
                ...prev, 
                showHint: !gameState.showHint,
                hintText: `Drag any icon to the left sphere to start matching!`
            }));
            setHintAutoShown(false); // Manual click, so don't auto-hide
        }
    };
    
    // Handle icon drag start
    const handleIconDragStart = (e) => {
        // Check if dragging is disabled
        if (!isDraggingEnabled) {
            e.preventDefault();
            return;
        }
        
        const iconId = e.currentTarget.dataset.iconId;
        
        // During onboarding, only allow dragging the highlighted icon
        if (showInteractiveOnboarding && onboardingStep > 0) {
            if (iconId !== allowedDragIconId) {
                e.preventDefault();
                return;
            }
        }
        
        const iconName = e.currentTarget.dataset.iconName;
        const productId = parseInt(e.currentTarget.dataset.productId);
        
        const icon = visibleIcons.find(i => i.id === iconId);
        setDraggedIcon(icon);
        
        // Create custom drag image without the highlight border
        const dragImage = e.currentTarget.cloneNode(true);
        dragImage.classList.remove('highlighted'); // Remove highlight class
        dragImage.classList.remove('correct-hint'); // Remove correct hint class
        dragImage.style.position = 'absolute';
        dragImage.style.top = '-1000px';
        dragImage.style.opacity = '0.8';
        document.body.appendChild(dragImage);
        
        // Set the custom drag image
        e.dataTransfer.setDragImage(dragImage, 22.5, 22.5);
        
        // Remove the temporary drag image after a short delay
        setTimeout(() => {
            document.body.removeChild(dragImage);
        }, 0);
        
        // Play icon pickup sound
        playSound(sounds.iconPickup);
        
        // Hide hint when user starts dragging
        setGameState(prev => ({ ...prev, showHint: false }));
        
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', iconId);
    };
    
    const handleIconDragEnd = () => {
        setDraggedIcon(null);
        if (leftSphereRef.current) {
            leftSphereRef.current.classList.remove('drag-over-correct', 'drag-over-incorrect');
        }
        if (rightSphereRef.current) {
            rightSphereRef.current.classList.remove('drag-over-correct', 'drag-over-incorrect');
        }
    };
    
    // Handle drag over left sphere
    const handleLeftDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        
        // Show drag-over indicator on left sphere
        if (draggedIcon && leftSphereRef.current && !leftIcon) {
            leftSphereRef.current.classList.add('drag-over-active');
        }
    };
    
    const handleLeftDragLeave = () => {
        // Remove drag-over indicator from left sphere
        if (leftSphereRef.current) {
            leftSphereRef.current.classList.remove('drag-over-active');
        }
    };
    
    // Handle drop on left sphere
    const handleLeftDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (leftSphereRef.current) {
            leftSphereRef.current.classList.remove('drag-over-active', 'drag-over-correct', 'drag-over-incorrect');
        }
        
        if (draggedIcon && !leftIcon) {
            // Play drop sound
            playSound(sounds.rightDrop);
            
            setLeftIcon(draggedIcon);
            // Add to usedIcons to show disabled state (but keep in visibleIcons so it's still visible)
            setUsedIcons(prev => [...prev, draggedIcon.id]);
            
            // During onboarding, progress to step 2 after first icon is dropped
            if (showInteractiveOnboarding && onboardingStep === 1) {
                setTimeout(() => {
                    // Find the computer icon (second icon in the pair)
                    const computerIcon = visibleIcons.find(icon => icon.iconName === 'computer');
                    if (computerIcon) {
                        setOnboardingStep(2);
                        setHighlightedIconId(computerIcon.id);
                        setAllowedDragIconId(computerIcon.id);
                        setHighlightedSphere('right');
                    }
                }, 500);
            }
        }
        
        setDraggedIcon(null);
    };
    
    // Handle drag over right sphere
    const handleRightDragOver = (e) => {
        e.preventDefault();
        
        // Only allow drag over if left sphere is filled
        if (!leftIcon) {
            e.dataTransfer.dropEffect = 'none';
            return;
        }
        
        e.dataTransfer.dropEffect = 'move';
        
        if (draggedIcon && rightSphereRef.current && !rightIcon && leftIcon) {
            // Check if it's a valid pair
            const pair = iconPairs.find(p => p.id === draggedIcon.productId);
            const isCorrectPair = pair && leftIcon && (
                (pair.icon1 === leftIcon.iconName && pair.icon2 === draggedIcon.iconName) ||
                (pair.icon2 === leftIcon.iconName && pair.icon1 === draggedIcon.iconName)
            );
            
            if (isCorrectPair) {
                rightSphereRef.current.classList.add('drag-over-correct');
                rightSphereRef.current.classList.remove('drag-over-incorrect');
            } else {
                rightSphereRef.current.classList.add('drag-over-incorrect');
                rightSphereRef.current.classList.remove('drag-over-correct');
            }
        }
    };
    
    const handleRightDragLeave = () => {
        if (rightSphereRef.current) {
            rightSphereRef.current.classList.remove('drag-over-correct', 'drag-over-incorrect');
        }
    };
    
    // Handle drop on right sphere
    const handleRightDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (rightSphereRef.current) {
            rightSphereRef.current.classList.remove('drag-over-correct', 'drag-over-incorrect');
        }
        
        if (draggedIcon && leftIcon && !rightIcon) {
            // Play right drop sound
            playSound(sounds.rightDrop);
            
            // Check if it's a correct pair
            const pair = iconPairs.find(p => p.id === draggedIcon.productId);
            const isCorrectPair = pair && (
                (pair.icon1 === leftIcon.iconName && pair.icon2 === draggedIcon.iconName) ||
                (pair.icon2 === leftIcon.iconName && pair.icon1 === draggedIcon.iconName)
            );
            
            if (isCorrectPair) {
                setRightIcon(draggedIcon);
                // Add to usedIcons to show disabled state
                setUsedIcons(prev => [...prev, draggedIcon.id]);
                setCurrentMatchedProduct(pair);
                
                // Disable dragging during match animation
                setIsDraggingEnabled(false);
                
                // Reset incorrect attempts, disable hint, and remove highlight for next round
                setIncorrectAttempts(0);
                setHintEnabled(false);
                setHighlightedIconId(null);
                setCorrectIconHintId(null); // Clear correct icon hint
                
                // During onboarding, complete the onboarding after second icon is dropped
                if (showInteractiveOnboarding && onboardingStep === 2) {
                    setOnboardingStep(3);
                    setHighlightedSphere(null);
                    setAllowedDragIconId(null);
                }
                
                handleCorrectMatch(pair);
            } else {
                handleIncorrectMatch();
            }
        }
        
        setDraggedIcon(null);
    };
    
    const handleCorrectMatch = (pair) => {
        // Play product merge sound
        playSound(sounds.productMerge);
        
        // Step 1: Apply merge animation classes immediately
        setMatchedCategory(leftIcon); // Set left icon for merge animation
        setHideSpheresForMerge(false); // Keep spheres visible during merge
        
        // Step 2: After merge animation completes (1 second), show blast
        setTimeout(() => {
            setGameState(prev => ({ ...prev, showSpark: true }));
            setHideSpheresForMerge(true);
        }, 500);
        // Step 3: After blast (1.2 seconds total), show result icon
        setTimeout(() => {
            // Add active class to app element when result icon shows
            const appElement = document.getElementById('app');
            if (appElement) {
                appElement.classList.add('active');
            }
            
            setShowResultIcon(true);
            setMatchedCategory(null);
            setHideSpheresForMerge(true); // Keep spheres hidden
        }, 1200);

        // Step 4: After showing result icon for 2 seconds (3.2 seconds total), fly to progress bar
        setTimeout(() => {
            console.log('🚀 Starting flying animation to specific product slot');
            
            // Get result icon position
            const resultIconElement = document.querySelector('.result-icon');
            
            // Find the specific keyhole slot for this product
            const targetSlot = document.querySelector(`.keyhole[data-slot="${pair.id}"]`);
            
            console.log('📍 Result icon element:', resultIconElement);
            console.log('📍 Target slot element:', targetSlot);
            console.log('📍 Target product ID:', pair.id);
            
            if (resultIconElement && targetSlot) {
                const resultIconRect = resultIconElement.getBoundingClientRect();
                const targetSlotRect = targetSlot.getBoundingClientRect();
                
                console.log('📍 Result icon position:', resultIconRect);
                console.log('📍 Target slot position:', targetSlotRect);
                
                // Start flying animation from result icon to the specific product slot
                setFlyingResultIcon({
                    product: pair,
                    startPos: {
                        top: resultIconRect.top + resultIconRect.height / 2 - 40,
                        left: resultIconRect.left + resultIconRect.width / 2 - 40
                    },
                    endPos: {
                        top: targetSlotRect.top + targetSlotRect.height / 2 - 40,
                        left: targetSlotRect.left + targetSlotRect.width / 2 - 40
                    }
                });
                
                console.log('✅ Flying result icon state set to slot', pair.id);
                
                // Hide the static result icon
                setShowResultIcon(false);
                setCurrentMatchedProduct(null);
                
                // After flying animation completes (0.6 seconds), update decoded list and show modal
                setTimeout(() => {
                    console.log('🎯 Flying animation complete, updating decoded list');
                    
                    // Play level completed sound when icon lands on decoded list
                    playSound(sounds.levelCompleted);
                    
                    // Remove flying icon
                    setFlyingResultIcon(null);
                    
                    // Icons are already in usedIcons from when they were placed on spheres
                    
                    // Update matched pairs count
                    const newMatchedPairs = [...matchedPairs, pair.id];
                    setMatchedPairs(newMatchedPairs);
                    
                    // Update decoded products list first
                    setGameState(prev => ({
                        ...prev,
                        completedProducts: newMatchedPairs.length,
                        unlockedSlots: [...prev.unlockedSlots, pair.id]
                    }));
                    
                    // After a brief delay (300ms) for the decoded list to update, show modal
                    setTimeout(() => {
                        console.log('🎉 Showing modal');
                        setGameState(prev => ({
                            ...prev,
                            showModal: true,
                            currentProduct: pair, // Set current product for modal
                            hasPendingNext: true // Flag that indicates "Find next" action is pending
                        }));
                    }, 300);
                }, 1000);
            } else {
                console.log('❌ Elements not found, using fallback');
                // Fallback if elements not found - show modal directly
                setShowResultIcon(false);
                setCurrentMatchedProduct(null);
                
                const newMatchedPairs = [...matchedPairs, pair.id];
                setMatchedPairs(newMatchedPairs);
                
                setGameState(prev => ({
                    ...prev,
                    completedProducts: newMatchedPairs.length,
                    unlockedSlots: [...prev.unlockedSlots, pair.id],
                    showModal: true,
                    currentProduct: pair
                }));
            }
        }, 4000);
    };
    
    const handleIncorrectMatch = () => {
        // Increment incorrect attempts
        const newAttempts = incorrectAttempts + 1;
        setIncorrectAttempts(newAttempts);
        
        // Enable hint after first incorrect attempt and trigger highlight
        if (!hintEnabled) {
            setHintEnabled(true);
            setHintHighlight(true); // Highlight the hint icon
        }
        
        // Auto-show hint on second failed attempt
        if (newAttempts >= 2 && leftIcon) {
            const pair = iconPairs.find(p => p.id === leftIcon.productId);
            if (pair) {
                const matchingIconName = pair.icon1 === leftIcon.iconName ? pair.icon2 : pair.icon1;
                
                // Play hint appear sound when auto-showing
                playSound(sounds.hintAppear);
                
                setGameState(prev => ({ 
                    ...prev, 
                    showHint: true,
                    hintText: pair.hint || `Look for the matching icon to complete ${pair.productName}!`
                }));
                setHintAutoShown(true); // Mark as auto-shown so it will auto-hide
                
                // Highlight the correct icon after 3rd attempt
                if (newAttempts >= 3) {
                    const correctIcon = visibleIcons.find(icon => 
                        icon.iconName === matchingIconName && icon.productId === leftIcon.productId
                    );
                    if (correctIcon) {
                        setCorrectIconHintId(correctIcon.id); // Use separate state for correct icon hint
                    }
                }
            }
        }
        
        // Add shake animation to right sphere
        if (rightSphereRef.current) {
            rightSphereRef.current.classList.add('shake');
            
            // Remove shake class after animation completes
            setTimeout(() => {
                if (rightSphereRef.current) {
                    rightSphereRef.current.classList.remove('shake');
                }
            }, 500);
        }
        
        // Animate icon flying back to pick list
        if (draggedIcon) {
            // Play icon return sound
            playSound(sounds.iconReturn);
            
            const iconElement = document.querySelector(`[data-icon-id="${draggedIcon.id}"]`);
            if (iconElement) {
                // Temporarily hide the icon in its grid position
                iconElement.style.visibility = 'hidden';
                
                // After a brief moment, show it with the fly-back animation
                setTimeout(() => {
                    iconElement.style.visibility = 'visible';
                    iconElement.classList.add('fly-back-to-list');
                    
                    // Don't remove the class - just let animation complete with forwards
                    // The class will be cleaned up when icon is used or game resets
                }, 100);
            }
        }
        
        // The dragged icon was never set as rightIcon, so it remains in the pick list
        // No need to clear anything - icon stays available for retry
    };
    
    const handleFindNext = () => {
        // Remove active class from app element
        const appElement = document.getElementById('app');
        if (appElement) {
            appElement.classList.remove('active');
        }
        
        setGameState(prev => ({ ...prev, fromDecodedList: false, viewingDuringMatch: false, hasPendingNext: false }));
        // Check if this was the 10th product (game complete after this)
        const isLastProduct = matchedPairs.length === iconPairs.length;
        
        if (isLastProduct) {
            // Show final congratulations screen
            setShowFinalCongrats(true);
            // Stop the timer
            setGameEndTime(Date.now());
            // Ensure modal is shown
            setGameState(prev => ({ ...prev, showModal: true }));
            
            // Stop all audio
            sounds.bgLoop.pause();
            sounds.bgLoop.currentTime = 0;
            
            // Play puzzle complete sound and resume background loop when it ends
            sounds.puzzleComplete.play().catch(err => console.log('Could not play puzzle complete sound:', err));
            
            // When puzzle complete sound ends, resume background loop
            sounds.puzzleComplete.onended = () => {
                if (gameState.soundEnabled) {
                    sounds.bgLoop.play().catch(err => console.log('BG music play failed:', err));
                }
            };
            return;
        }
        
        setGameState(prev => ({ ...prev, showModal: false }));
        
        setTimeout(() => {
            // Handle onboarding case where leftIcon/rightIcon might be null
            // Use currentProduct to find the matched icons
            if (!leftIcon || !rightIcon) {
                if (gameState.currentProduct) {
                    // Find the icons that match the current product
                    const productIcons = visibleIcons.filter(
                        icon => icon.productId === gameState.currentProduct.id
                    );
                    
                    // Remove the matched pair from visible icons
                    const newVisibleIcons = visibleIcons.filter(
                        icon => icon.productId !== gameState.currentProduct.id
                    );
                    
                    // If there are remaining pairs in queue, add the next pair
                    if (remainingIcons.length >= 2) {
                        const nextPairProductId = remainingIcons[0].productId;
                        const nextPairIcons = remainingIcons.filter(icon => icon.productId === nextPairProductId);
                        const updatedVisibleIcons = [...newVisibleIcons, ...nextPairIcons];
                        const updatedRemainingIcons = remainingIcons.filter(icon => icon.productId !== nextPairProductId);
                        
                        setVisibleIcons(shuffleArray(updatedVisibleIcons));
                        setRemainingIcons(updatedRemainingIcons);
                        playLevelUpSound(); // Play sound when new icons are added
                    } else {
                        setVisibleIcons(shuffleArray(newVisibleIcons));
                    }
                    
                    // Clear usedIcons for the matched product
                    if (productIcons.length > 0) {
                        setUsedIcons(prev => prev.filter(id => !productIcons.some(icon => icon.id === id)));
                    }
                }
                
                // Reset sphere states
                setLeftIcon(null);
                setRightIcon(null);
                setHideSpheresForMerge(false);
                setShowResultIcon(false);
                setMatchedCategory(null);
                
                // Re-enable dragging for next round
                setIsDraggingEnabled(true);
                return;
            }
            
            // Normal game flow (not onboarding)
            // Queue logic: Remove matched pair from visible icons
            const newVisibleIcons = visibleIcons.filter(
                icon => icon.id !== leftIcon.id && icon.id !== rightIcon.id
            );
            
            // If there are remaining pairs in queue, add the next pair
            if (remainingIcons.length >= 2) {
                // Get the first product from remaining queue
                const nextPairProductId = remainingIcons[0].productId;
                const nextPairIcons = remainingIcons.filter(icon => icon.productId === nextPairProductId);
                
                // Add the complete pair (2 icons) to visible - keeps count at 10
                const updatedVisibleIcons = [...newVisibleIcons, ...nextPairIcons];
                
                // Remove the added pair from remaining pool
                const updatedRemainingIcons = remainingIcons.filter(icon => icon.productId !== nextPairProductId);
                
                setVisibleIcons(shuffleArray(updatedVisibleIcons));
                setRemainingIcons(updatedRemainingIcons);
                playLevelUpSound(); // Play sound when new icons are added
            } else {
                // Queue is empty - just show remaining icons (count will decrease)
                setVisibleIcons(shuffleArray(newVisibleIcons));
            }
            
            // Clear usedIcons - remove the matched icons since they're no longer visible
            setUsedIcons(prev => prev.filter(id => id !== leftIcon.id && id !== rightIcon.id));
            
            // Reset sphere states
            setLeftIcon(null);
            setRightIcon(null);
            setHideSpheresForMerge(false);
            setShowResultIcon(false);
            setMatchedCategory(null);
            
            // Re-enable dragging for next round
            setIsDraggingEnabled(true);
            
            // Check if game is complete (all 10 pairs matched)
            if (matchedPairs.length === iconPairs.length) {
                setGameState(prev => ({ 
                    ...prev, 
                    showModal: true,
                    currentProduct: null // Show game complete modal
                }));
            }
        }, 300);
    };
    
    const handlePlayAgain = () => {
        window.location.reload();
    };
    
    // Show congratulations only when explicitly triggered, not just when all products are matched
    const isGameComplete = showFinalCongrats;
    
    return (
        <>
            {/* {!gameState.initialScreen && gameStartTime && !gameState.showModal && (
                <div className="game-timer">
                    ⏱️ {Math.floor(elapsedTime / 60)}:{(elapsedTime % 60).toString().padStart(2, '0')}
                </div>
            )} */}
            {!gameState.initialScreen && (
                <div className='progress-container-wrapper'>
                <ProgressBar 
                    completedProducts={matchedPairs.length}
                    unlockedSlots={gameState.unlockedSlots}
                    onProductClick={handleProductClick}
                    isModalOpen={gameState.showModal && !gameState.fromDecodedList}
                />
                {!gameState.showModal && !showFinalCongrats && (
                    <Controls
                    soundEnabled={gameState.soundEnabled}
                    onToggleSound={handleToggleSound}
                    onShowHint={handleShowHint}
                    showHint={gameState.showHint}
                    hintEnabled={hintEnabled}
                    hintHighlight={hintHighlight}
                    />
                )}
                {gameState.showHint && (
                    <HintTooltip 
                        show={gameState.showHint}
                        hintText={gameState.hintText}
                    />
                )}
                <ProductModal 
                    show={gameState.showModal || showFinalCongrats}
                    currentProduct={gameState.currentProduct}
                    isGameComplete={isGameComplete}
                    isLastProduct={matchedPairs.length === iconPairs.length && !showFinalCongrats}
                    onFindNext={
                        // If viewingDuringMatch is true, always use handleFindNext (user is viewing decoded product during a match)
                        gameState.viewingDuringMatch ? handleFindNext :
                        // If fromDecodedList but NOT viewingDuringMatch, just close modal
                        (gameState.fromDecodedList ? () => setGameState(prev => ({ ...prev, showModal: false, fromDecodedList: false, viewingDuringMatch: false })) : 
                        // Onboarding case or normal match flow
                        (showInteractiveOnboarding ? () => {
                            setGameState(prev => ({ ...prev, showModal: false }));
                            setTimeout(() => {
                                handleCompleteOnboarding();
                            }, 100);
                        } : handleFindNext))
                    }
                    onPlayAgain={handlePlayAgain}
                    fromDecodedList={gameState.fromDecodedList}
                    hasPendingNext={gameState.hasPendingNext}
                    viewingDuringMatch={gameState.viewingDuringMatch}
                    elapsedTime={elapsedTime}
                />
                </div>
            )}
            
            {isFullscreen && <CloseFullscreenButton onExitFullscreen={handleExitFullscreen} />}
            
            {gameState.initialScreen && <InitialScreen />}
            
            {gameState.initialScreen && <PlayNowButton onClick={handleStartGame} />}
            
            {!gameState.initialScreen && !gameState.showModal && !showFinalCongrats && (
                <div className={`game-container ${(gameState.showSpark || hideSpheresForMerge) ? 'hide-spheres' : ''}`}>
                    <Platform 
                        leftIcon={leftIcon}
                        rightIcon={rightIcon}
                        matchedCategory={matchedCategory}
                        isMatching={!!matchedCategory}
                        leftSphereRef={leftSphereRef}
                        rightSphereRef={rightSphereRef}
                        onLeftDragOver={handleLeftDragOver}
                        onLeftDragLeave={handleLeftDragLeave}
                        onLeftDrop={handleLeftDrop}
                        onRightDragOver={handleRightDragOver}
                        onRightDragLeave={handleRightDragLeave}
                        onRightDrop={handleRightDrop}
                        highlightedSphere={highlightedSphere}
                    />
                </div>
            )}
            {!gameState.initialScreen && !gameState.showModal && !showFinalCongrats && (
                <div className="bottom-section">
                    {/* <Controls 
                        soundEnabled={gameState.soundEnabled}
                        onToggleSound={handleToggleSound}
                        onShowHint={handleShowHint}
                    /> */}
                    
                    <CategoryIcons 
                        visibleIcons={visibleIcons}
                        usedIcons={usedIcons}
                        onDragStart={handleIconDragStart}
                        onDragEnd={handleIconDragEnd}
                        highlightedIconId={highlightedIconId}
                        correctIconHintId={correctIconHintId}
                        leftIcon={leftIcon}
                        isDraggingDisabled={false}
                    />
                </div>
            )}
            {!gameState.initialScreen && (
                <>
                    <SparkOverlay show={gameState.showSpark} />
                    
                    <ResultIcon show={showResultIcon} product={currentMatchedProduct} />
                    
                    {/* Flying result icon to progress bar */}
                    {flyingResultIcon && (
                        <div 
                            className="flying-result-icon"
                            style={{
                                backgroundImage: `url('/images/SVG/${flyingResultIcon.product.productIcon}.svg')`,
                                top: `${flyingResultIcon.endPos.top}px`,
                                left: `${flyingResultIcon.endPos.left}px`,
                                opacity: 0,
                                transform: `translate(${flyingResultIcon.startPos.left - flyingResultIcon.endPos.left}px, ${flyingResultIcon.startPos.top - flyingResultIcon.endPos.top}px) scale(1)`
                            }}
                        />
                    )}
                </>
            )}
            
            <Instructions 
                show={gameState.showInstructions}
                onClose={handleCloseInstructions}
            />
            
            <VideoOnboarding 
                show={showVideoOnboarding}
                onVideoComplete={handleVideoComplete}
                onComplete={handleCompleteOnboarding}
                videoUrl="/videos/onboarding.mp4"
            />
            
            {gameState.initialScreen &&(<Logo />)}

        </>
    );
}
